/**
 * The draft state behind "chế độ xếp lớp" (bulk move mode) on the Thời khoá
 * biểu screen — pure state transitions only, no React and no fetch. The
 * component renders this; it does not reimplement any of its rules.
 *
 * Design (docs/superpowers/specs/2026-08-14-slot-move-mode-design.md): a
 * teacher ticks students in exactly one slot ("the source"), picks or drags
 * a target slot, and confirms — one call to `POST /api/slots/move` with
 * `(fromSlotId, toSlotId, enrollmentIds)`. Single-source is not a UI nicety:
 * it is what keeps the API to that one triple. Two sources would mean two
 * `fromSlotId`s, a shape the batch endpoint does not accept — so the draft
 * enforces one source before a request is ever built, rather than letting
 * the server reject a shape the UI should never have sent.
 */

export interface SlotMoveCandidate {
  id: string;
  subjectId: string;
  status: 'ACTIVE' | 'ARCHIVED';
}

export interface SlotMoveDraft {
  /** The one slot ticks are currently coming from, or null once nothing is
   *  ticked anywhere. */
  readonly sourceSlotId: string | null;
  /** enrollmentIds ticked in `sourceSlotId`. Always empty when `sourceSlotId`
   *  is null — the two fields rise and fall together. */
  readonly selected: readonly string[];
  /** The chosen destination — set by either the picker or a drop, always
   *  through `setTarget` (see there). Null until one of those fires. */
  readonly targetSlotId: string | null;
}

export const emptyMoveDraft: SlotMoveDraft = { sourceSlotId: null, selected: [], targetSlotId: null };

/**
 * Tick/untick one student in `slotId`. A tick outside the current source is
 * rejected outright — the draft comes back unchanged — rather than switching
 * sources for the teacher: they must clear slot A before ticking in slot B,
 * so the UI never has to guess which slot they meant.
 *
 * Clearing the last tick resets the *whole* draft, target included: an empty
 * selection with a leftover target would let a stale destination survive
 * into the next group's pick, which is exactly what the bottom bar's
 * "cancel" is supposed to prevent — untick-to-zero is cancel by another
 * route, so it has to behave the same way.
 */
export function toggleTick(draft: SlotMoveDraft, slotId: string, enrollmentId: string): SlotMoveDraft {
  if (!canTickInSlot(draft, slotId)) return draft;
  const isTicked = draft.selected.includes(enrollmentId);
  const selected = isTicked
    ? draft.selected.filter((id) => id !== enrollmentId)
    : [...draft.selected, enrollmentId];
  if (selected.length === 0) return emptyMoveDraft;
  return { sourceSlotId: slotId, selected, targetSlotId: draft.targetSlotId };
}

/** Whether `slotId` may currently accept a tick: true for the source itself,
 *  and for every slot while nothing is ticked anywhere yet. Drives which
 *  chips render enabled outside the source slot's own card. */
export function canTickInSlot(draft: SlotMoveDraft, slotId: string): boolean {
  return draft.sourceSlotId === null || draft.sourceSlotId === slotId;
}

/**
 * Same-subject, ACTIVE, and not the source itself — the three guards
 * `POST /api/slots/move` enforces server-side, mirrored here so the UI can
 * filter the picker's list and decide which slot cards get wired up as drop
 * targets *before* a request is ever sent, not just after a 400 comes back.
 */
export function isEligibleTarget(source: SlotMoveCandidate, candidate: SlotMoveCandidate): boolean {
  return candidate.id !== source.id && candidate.status === 'ACTIVE' && candidate.subjectId === source.subjectId;
}

/**
 * Sets the draft's target. This is the one function both the mobile picker
 * and the desktop drop call — by construction, whichever gesture a teacher
 * used, the resulting draft is identical, so nothing downstream (the bottom
 * bar, the confirm mutation) needs to know or care which one it was.
 *
 * A candidate failing `isEligibleTarget` is ignored (draft unchanged): the
 * picker only ever lists eligible slots, and an ineligible card is never
 * registered as a droppable in the first place, so reaching this branch
 * would mean some caller bypassed both routes — the function still holds
 * the line rather than trusting the caller.
 */
export function setTarget(
  draft: SlotMoveDraft,
  source: SlotMoveCandidate,
  candidate: SlotMoveCandidate,
): SlotMoveDraft {
  if (draft.sourceSlotId === null) return draft;
  if (!isEligibleTarget(source, candidate)) return draft;
  return { ...draft, targetSlotId: candidate.id };
}

/** Leaving move mode (or hitting "cancel" in the bottom bar) discards the
 *  draft entirely — nothing carries over into the next time the mode opens. */
export function clearDraft(): SlotMoveDraft {
  return emptyMoveDraft;
}
